import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import CompaniesPage from "@/pages/CompaniesPage";
import CandidatesPage from "@/pages/CandidatesPage";

import LeadsPage from "@/pages/LeadsPage";
import EnquiriesPage from "@/pages/EnquiriesPage";
import DialerPage from "@/pages/DialerPage";
import CallLogsPage from "@/pages/CallLogsPage";
import TenantsPage from "@/pages/TenantsPage";
import UsersPage from "@/pages/UsersPage";
import SettingsPage from "@/pages/SettingsPage";
import TeamsManagementPage from "@/pages/TeamsManagementPage";
import FeatureTogglesPage from "@/pages/FeatureTogglesPage";
import EmailsPage from "@/pages/EmailsPage";
import SearchCentrePage from "@/pages/SearchCentrePage";
import InvoicesPage from "@/pages/InvoicesPage";
import CommissionPage from "@/pages/CommissionPage";
import TalentPoolsPage from "@/pages/TalentPoolsPage";
import VacanciesPage from "@/pages/VacanciesPage";
import PlacementPipelinePage from "@/pages/PlacementPipelinePage";
import InterviewsPage from "@/pages/InterviewsPage";
import InterviewPipelinePage from "@/pages/InterviewPipelinePage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import ActivityLogPage from "@/pages/ActivityLogPage";
import SectorHubPage from "@/pages/SectorHubPage";
import WorkflowPage from "@/pages/WorkflowPage";
import TelephonySettingsPage from "@/pages/TelephonySettingsPage";
import ContractsPage from "@/pages/ContractsPage";
import SignContractPage from "@/pages/SignContractPage";
import AcademyPage from "@/pages/AcademyPage";
import AcademyAdminPage from "@/pages/AcademyAdminPage";
import OnboardingRegisterPage from "@/pages/OnboardingRegisterPage";
import OnboardingPage from "@/pages/OnboardingPage";
import OnboardingAdminPage from "@/pages/OnboardingAdminPage";
import ScriptsPage from "@/pages/ScriptsPage";
import DailyPlannerPage from "@/pages/DailyPlannerPage";
import MessagingPage from "@/pages/MessagingPage";
import BriefingsPage from "@/pages/BriefingsPage";
import TeamResourcesPage from "@/pages/TeamResourcesPage";
import DistributionPage from "@/pages/DistributionPage";
import TeamStatusPage from "@/pages/TeamStatusPage";
import IntelligencePage from "@/pages/IntelligencePage";
import CandidateEnginePage from "@/pages/CandidateEnginePage";
import PartnershipPricingPage from "@/pages/PartnershipPricingPage";
import PublicSiteLayout, { HomePage as PublicHome, JobsPage as PublicJobs, JobDetailPage as PublicJobDetail, SectorsPage as PublicSectors, SectorDetailPage as PublicSectorDetail, CandidatesPage as PublicCandidates, EmployersPage as PublicEmployers, PartnershipsPage as PublicPartnerships, AboutPage as PublicAbout, ContactPage as PublicContact, JobAlertUnsubscribePage as PublicUnsubscribe } from "@/pages/PublicSite";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-[#F9FAFB]">
      <div className="flex flex-col items-center gap-3">
        <div className="bm-logo-mark text-lg">BM</div>
        <div className="w-8 h-8 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );
  if (!user) return <Navigate to="/hub/login" replace />;
  return children;
}

function OnboardingGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  // Admin bypasses onboarding gate
  if (user?.role === "admin") return children;
  // Allow Academy access during onboarding (agreements_signed phase)
  if (location.pathname.startsWith("/hub/academy")) return children;
  // If user hasn't completed onboarding, redirect to onboarding
  const status = user?.onboarding_status;
  if (status && status !== "active") {
    return <Navigate to="/hub/onboarding" replace />;
  }
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return (
    <Routes>
      {/* Hub — protected routes at /hub */}
      <Route path="/hub/login" element={user ? <Navigate to="/hub" replace /> : <LoginPage />} />
      <Route path="/hub/forgot-password" element={user ? <Navigate to="/hub" replace /> : <ForgotPasswordPage />} />
      <Route path="/hub/sign-contract/:token" element={<SignContractPage />} />
      <Route path="/hub/onboarding/:token" element={<OnboardingRegisterPage />} />
      <Route path="/hub/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/hub" element={<ProtectedRoute><OnboardingGate><Layout /></OnboardingGate></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="sector-hub" element={<SectorHubPage />} />
        <Route path="workflow" element={<WorkflowPage />} />
        <Route path="companies" element={<CompaniesPage />} />
        <Route path="candidates" element={<CandidatesPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="enquiries" element={<EnquiriesPage />} />
        <Route path="dialer" element={<DialerPage />} />
        <Route path="call-logs" element={<CallLogsPage />} />
        <Route path="teams" element={<TeamsManagementPage />} />
        <Route path="emails" element={<EmailsPage />} />
        <Route path="search" element={<SearchCentrePage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="commission" element={<CommissionPage />} />
        <Route path="talent-pools" element={<TalentPoolsPage />} />
        <Route path="vacancies" element={<VacanciesPage />} />
        <Route path="pipeline" element={<PlacementPipelinePage />} />
        <Route path="interviews" element={<InterviewsPage />} />
        <Route path="interview-pipeline" element={<InterviewPipelinePage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="activity-log" element={<ActivityLogPage />} />
        <Route path="feature-toggles" element={<FeatureTogglesPage />} />
        <Route path="tenants" element={<TenantsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="telephony-settings" element={<TelephonySettingsPage />} />
        <Route path="contracts" element={<ContractsPage />} />
        <Route path="academy" element={<AcademyPage />} />
        <Route path="academy-admin" element={<AcademyAdminPage />} />
        <Route path="onboarding-admin" element={<OnboardingAdminPage />} />
        <Route path="scripts" element={<ScriptsPage />} />
        <Route path="planner" element={<DailyPlannerPage />} />
        <Route path="messaging" element={<MessagingPage />} />
        <Route path="briefings" element={<BriefingsPage />} />
        <Route path="team-resources" element={<TeamResourcesPage />} />
        <Route path="distribution" element={<DistributionPage />} />
        <Route path="team-status" element={<TeamStatusPage />} />
        <Route path="kpi-dashboard" element={<Navigate to="/hub/team-status" replace />} />
        <Route path="intelligence" element={<IntelligencePage />} />
        <Route path="candidate-engine" element={<CandidateEnginePage />} />
        <Route path="partnership-pricing" element={<PartnershipPricingPage />} />
      </Route>

      {/* Public Website — no auth required, at root */}
      <Route path="/jobs" element={<PublicSiteLayout><PublicJobs /></PublicSiteLayout>} />
      <Route path="/jobs/:id" element={<PublicSiteLayout><PublicJobDetail /></PublicSiteLayout>} />
      <Route path="/sectors" element={<PublicSiteLayout><PublicSectors /></PublicSiteLayout>} />
      <Route path="/sectors/:slug" element={<PublicSiteLayout><PublicSectorDetail /></PublicSiteLayout>} />
      <Route path="/candidates" element={<PublicSiteLayout><PublicCandidates /></PublicSiteLayout>} />
      <Route path="/employers" element={<PublicSiteLayout><PublicEmployers /></PublicSiteLayout>} />
      <Route path="/partnerships" element={<PublicSiteLayout><PublicPartnerships /></PublicSiteLayout>} />
      <Route path="/about" element={<PublicSiteLayout><PublicAbout /></PublicSiteLayout>} />
      <Route path="/contact" element={<PublicSiteLayout><PublicContact /></PublicSiteLayout>} />
      <Route path="/job-alerts/unsubscribe/:token" element={<PublicSiteLayout><PublicUnsubscribe /></PublicSiteLayout>} />
      <Route path="/" element={<PublicSiteLayout><PublicHome /></PublicSiteLayout>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
